import { useKeyboardNavigation } from "@mirage/mosaics/GlobalNav/useKeyboardNavigation";
import type { TypeaheadResult } from "@mirage/mosaics/SearchBarWithTypeahead/useConvertToTypeaheadResults";
import { typeahead } from "@mirage/service-typeahead-search/service/types";
import type { Handler } from "@mirage/shared/hotkeys";
import { preventBubble } from "@mirage/shared/hotkeys";
import { useCallback, useEffect, useState } from "react";

type Props = {
  userQueryResultIndex: number;
  omniboxScopedMode: typeahead.Mode;
  typeaheadQuery: string;
  searchResults: TypeaheadResult[];
  typeaheadResults: typeahead.ScoredResult[];
  canShowTypeahead: boolean;
};

export function useSelectedIndex({
  userQueryResultIndex,
  omniboxScopedMode,
  typeaheadQuery,
  searchResults,
  typeaheadResults,
  canShowTypeahead,
}: Props) {
  const isInSearchFilters = searchResults.some(
    (result) => result.scoredResult.type === typeahead.ResultType.SearchFilter,
  );
  const isInURLShortcuts = searchResults.some(
    (result) => result.scoredResult.type === typeahead.ResultType.URLShortcut,
  );

  const { moveFocusToResults } = useKeyboardNavigation();

  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  const searchResultsLazyHash = hasResultsChanged(searchResults);

  useEffect(() => {
    setSelectedIndex(
      omniboxScopedMode === typeahead.Mode.Default &&
        typeaheadQuery.length > 0 &&
        !isInSearchFilters &&
        !isInURLShortcuts
        ? (userQueryResultIndex ?? 0)
        : 0,
    );
  }, [
    setSelectedIndex,
    searchResultsLazyHash,
    omniboxScopedMode,
    userQueryResultIndex,
    typeaheadQuery.length,
    isInSearchFilters,
    isInURLShortcuts,
  ]);

  useEffect(() => {
    if (canShowTypeahead) return;
    setSelectedIndex(0);
  }, [setSelectedIndex, canShowTypeahead]);

  const moveUp = useCallback<Handler>(
    (e) => {
      if (!canShowTypeahead) return;
      preventBubble(e);
      setSelectedIndex((prevIdx) => handlePrev(prevIdx, searchResults.length));
    },
    [canShowTypeahead, setSelectedIndex, searchResults.length],
  );

  const moveDown = useCallback<Handler>(
    (e) => {
      if (!canShowTypeahead || !typeaheadResults.length) {
        preventBubble(e);
        moveFocusToResults();
        return;
      }
      setSelectedIndex((prevIdx) => handleNext(prevIdx, searchResults.length));
    },
    [
      canShowTypeahead,
      setSelectedIndex,
      searchResults.length,
      moveFocusToResults,
      typeaheadResults.length,
    ],
  );

  const setSelectedIndexToUserQueryResult = useCallback(() => {
    setSelectedIndex(userQueryResultIndex);
  }, [userQueryResultIndex]);

  return {
    selectedIndex,
    moveUp,
    moveDown,
    setSelectedIndexToUserQueryResult,
  };
}

export function hasResultsChanged(results: TypeaheadResult[]): string {
  return results.reduce((acc, result) => acc + result.uuid + result.title, "");
}

export function handleNext(current: number, numResults: number): number {
  if (numResults === 0) return 0;
  if (numResults === 1) return 0;
  return (current + 1) % numResults;
}

export function handlePrev(current: number, numResults: number): number {
  if (numResults === 0) return 0;
  if (numResults === 1) return 0;
  if (current < 0) return numResults - 1;
  return (current - 1 + numResults) % numResults;
}
